import getWindow from 'get-window';

import { TextRange as SlateRange, type Value } from '@gitbook/slate';
import { IS_CHROME, IS_EDGE, IS_IE } from '@gitbook/slate-dev-environment';

import findDOMPoint from './find-dom-point';
import findPoint from './find-point';
import { isHangingRange } from './is-hanging-range';
import { isSelectionBackward } from './isSelectionBackward';
import { unhangRange } from './unhang-range';

/*
 * Find a Slate range from a DOM `native` selection.
 *
 * @param {Selection} native
 * @param {Value} value
 * @return {Range}
 */
export default function findRange(
    native: Selection | Range | StaticRange | null,
    value: Value
): SlateRange | null {
    if (!native) {
        return null;
    }

    const el = 'anchorNode' in native ? native.anchorNode : native.startContainer;
    if (!el) {
        return null;
    }

    const window = getWindow(el);

    // If the `native` object is a DOM `Range` or `StaticRange` object, change it
    // into something that looks like a DOM `Selection` instead.
    if (
        native instanceof window.Range ||
        (window.StaticRange && native instanceof window.StaticRange)
    ) {
        // @ts-expect-error
        native = {
            // @ts-expect-error
            anchorNode: native.startContainer,
            // @ts-expect-error
            anchorOffset: native.startOffset,
            // @ts-expect-error
            focusNode: native.endContainer,
            // @ts-expect-error
            focusOffset: native.endOffset,
        };
    }

    // @ts-expect-error
    const { anchorNode, anchorOffset, focusNode, focusOffset, isCollapsed } = native;
    const anchor = findPoint(anchorNode, anchorOffset, value);
    const focus = isCollapsed ? anchor : findPoint(focusNode, focusOffset, value);
    if (!anchor || !focus) {
        return null;
    }

    // COMPAT: ??? The Edge browser seems to have a case where if you select the
    // last word of a span, it sets the endContainer to the containing span.
    // `selection-is-backward` doesn't handle this case.
    if (IS_IE || IS_EDGE) {
        const domAnchor = findDOMPoint(anchor.key, anchor.offset);
        const domFocus = findDOMPoint(focus.key, focus.offset);

        if (domAnchor && domFocus) {
            // @ts-expect-error
            native = {
                anchorNode: domAnchor.node,
                anchorOffset: domAnchor.offset,
                focusNode: domFocus.node,
                focusOffset: domFocus.offset,
            };
        }
    }

    let range = SlateRange.create({
        anchorKey: anchor.key,
        anchorOffset: anchor.offset,
        focusKey: focus.key,
        focusOffset: focus.offset,
        // @ts-expect-error
        isBackward: !isCollapsed && isSelectionBackward(native),
        isFocused: true,
    });

    /**
     * COMPAT: There's a bug in Chromium if you triple click towards the end of the word hoping to select the whole word
     * then the selection returned by `window.getSelection()` will be incorrect. It returns a "hanging" range
     * which starts from the start of the current block to the start of next block.
     * Reference: https://github.com/ianstormtaylor/slate/issues/4862#issuecomment-1276042578
     *
     * The fix in this case is to "unhang" the range by setting the anchor to the start of the anchor parent
     * and the focus to the end of the anchor parent block.
     */
    if ((IS_CHROME || IS_EDGE) && isHangingRange(range, value.document)) {
        range = unhangRange(range, value.document);
    }

    return range;
}
